/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext, useEffect } from 'react';

import { content } from 'libs/http/api/content/content';
import { useRouter } from 'libs/router/useRouter';
import type { Page } from 'libs/http/api/common/common.types';

import { ContentContext } from 'context/ContentContext';

import { useRequest } from 'hooks/useRequest';

import { Section } from 'ui/molecules/Section/Section';
import { SliderShowcase } from 'ui/atoms/SliderShowcase/SliderShowcase';
import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './CustomerSuccess.module.scss';

type Props = {
  title: string;
  description: string;
  kicker?: string;
  className?: string;
};

export const CustomerSuccess = ({ title, description, kicker, className }: Props) => {
  const { secondaryNavigation } = useContext(ContentContext);
  const router = useRouter();

  const { data: caseStudies, loading, request } = useRequest<Page[]>({ data: [] });

  useEffect(() => {
    request(content.articlesList({ lang: router.query.lang as string, case_studies_type: '', page_size: 6 }));
  }, [request, router.query.lang]);

  const slides = caseStudies
    .filter((page) => page.background?.url)
    .map((page) => ({
      src: page.background!.url,
      logos: [] as string[],
    }));

  const children = caseStudies
    .filter((page) => page.background?.url)
    .map((page, idx) => (
      <div key={idx}>
        <p>{page.title}</p>
        <a href={page.slug}>
          Read more <span>→</span>
        </a>
      </div>
    ));

  if (!loading && slides.length === 0) {
    return null;
  }

  return (
    <Section className={className}>
      <div className={styles.grid}>
        <div className={styles.left}>
          <TitleBlock
            align="left"
            title={title}
            description={description}
            extra={kicker ? <p className={styles.kicker}>{kicker}</p> : undefined}
            className={styles.intro}
          />

          <Button type="blue" size="xl" route={secondaryNavigation?.customers?.slug}>
            Find out more
          </Button>
        </div>

        <div>
          {slides.length > 0 ? (
            <SliderShowcase slides={slides} className={styles.slider}>
              {children}
            </SliderShowcase>
          ) : (
            <div className={styles.skeleton} />
          )}
        </div>
      </div>
    </Section>
  );
};
